import {
  ACTION_CARD_KINDS,
  AskUserContentSchema,
} from '@littlebird/action-cards';
import { useStores, type ActionCard } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';

import { YStack } from '@/components/ui/layout/layout';

import { submitAskUserAnswers } from './ask-user.commands';
import { askUserStore } from './ask-user.store';
import { AskUserAnswersSummary } from './AskUserAnswersSummary';
import { AskUserPicker } from './AskUserPicker';

import type { ComponentProps, ReactNode } from 'react';

export const AskUserActionCardBody = observer(
  ({
    card,
    onComplete,
    renderHeader,
    context,
    autoFocus,
  }: {
    card: ActionCard;
    context?: ReactNode;
    onComplete?: () => void;
    renderHeader?: ComponentProps<typeof AskUserPicker>['renderHeader'];
    autoFocus?: boolean;
  }) => {
    const { messages } = useStores();
    const request = AskUserContentSchema.safeParse(card.content).data;
    if (!request) return null;
    const submitting = askUserStore.isSubmitting(request.request_id);
    // Sent answers show at once; the card completes only after the backend saves them and sync arrives
    const sentAnswers =
      card.status === 'draft' && askUserStore.isSent(request.request_id)
        ? askUserStore.buildAnswers(request)
        : null;
    const userMessageId = card.result?.user_message_id;
    const answers =
      typeof userMessageId === 'string'
        ? messages.get(userMessageId)?.askUserAnswers
        : null;

    if (sentAnswers) {
      return (
        <>
          {renderHeader?.()}
          <YStack className="gap-2">
            <AskUserAnswersSummary answers={sentAnswers} />
            <span className="text-sm text-foreground-muted">
              {ACTION_CARD_KINDS.ask_user.runningLabel}
            </span>
          </YStack>
        </>
      );
    }

    if (card.status !== 'draft') {
      return (
        <>
          {renderHeader?.()}
          {answers ? (
            <AskUserAnswersSummary answers={answers} />
          ) : (
            <YStack className="gap-2 p-4">
              {request.questions.map((question) => (
                <span key={question.id}>{question.prompt}</span>
              ))}
              <span className="text-sm text-foreground-muted">
                {card.status === 'completed'
                  ? 'Answered'
                  : 'No longer waiting for an answer'}
              </span>
            </YStack>
          )}
        </>
      );
    }

    const picker = (
      <AskUserPicker
        key={request.request_id}
        request={request}
        renderHeader={renderHeader}
        context={context}
        disabled={submitting}
        autoFocus={autoFocus}
        onComplete={() => void submitAskUserAnswers(card, onComplete)}
      />
    );
    return renderHeader ? (
      picker
    ) : (
      <fieldset disabled={submitting} inert={submitting} aria-busy={submitting}>
        {picker}
      </fieldset>
    );
  }
);
